import type { GuidedImportPack } from './guidedImportTemplate.js';
import type { ImportDestination } from './openingImport.js';

export interface ReviewRow {
  readonly rowId: string;
  readonly packId: string;
  readonly sheetId: string;
  readonly sourceRow: number;
  readonly values: Readonly<Record<string, unknown>>;
  readonly errors: readonly (string | { readonly field?: string; readonly message: string })[];
}
export interface ReviewRelation {
  readonly proposalId: string;
  readonly leftRowId: string;
  readonly rightRowId: string;
  readonly source?: 'identity' | 'smart' | 'manual';
  readonly probability: number | null;
  readonly confidence: number | string | null;
  readonly decision: 'pending' | 'link' | 'separate';
  readonly strongConflict: boolean;
}
export interface ReviewWorkspace {
  readonly rows: readonly ReviewRow[];
  readonly proposals: readonly ReviewRelation[];
  readonly files?: readonly { readonly packId: string; readonly fileName: string }[];
  readonly groups?: readonly {
    readonly canonicalRowId: string;
    readonly rowIds: readonly string[];
  }[];
  readonly referenceLinks?: readonly { readonly fromRowId: string; readonly toRowId: string }[];
  readonly status?: string;
  readonly result?: { readonly destinations?: readonly ImportDestination[] } | null;
}
export type ConfidenceBucket = 'high' | 'medium' | 'low' | 'unscored';
const unitInterval = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1 ? value : null;

/** These are display bands, not calibrated probabilities of an error-free import. */
export function relationConfidence(relation: ReviewRelation) {
  const source = relation.source ?? 'unknown';
  const confidence = source === 'smart' ? unitInterval(relation.confidence) : null;
  const probability = source === 'smart' ? unitInterval(relation.probability) : null;
  const bucket: ConfidenceBucket =
    confidence === null
      ? 'unscored'
      : confidence >= 0.85
        ? 'high'
        : confidence >= 0.6
          ? 'medium'
          : 'low';
  return {
    source,
    label:
      source === 'smart'
        ? 'پیشنهاد JEV'
        : source === 'identity'
          ? 'تطبیق شناسه'
          : source === 'manual'
            ? 'بررسی دستی'
            : 'منبع نامشخص',
    confidence,
    probability,
    bucket,
  };
}

function modelCoverage(relations: readonly ReviewRelation[], sectionRows?: ReadonlySet<string>) {
  const buckets: Record<ConfidenceBucket, number> = { high: 0, medium: 0, low: 0, unscored: 0 };
  const coveredRows = new Set<string>();
  let modelPairs = 0;
  let identityPairs = 0;
  let manualPairs = 0;
  let unknownPairs = 0;
  for (const relation of relations) {
    const score = relationConfidence(relation);
    buckets[score.bucket]++;
    if (score.source === 'smart') modelPairs++;
    else if (score.source === 'identity') identityPairs++;
    else if (score.source === 'manual') manualPairs++;
    else unknownPairs++;
    if (score.confidence !== null) {
      if (!sectionRows || sectionRows.has(relation.leftRowId)) coveredRows.add(relation.leftRowId);
      if (!sectionRows || sectionRows.has(relation.rightRowId))
        coveredRows.add(relation.rightRowId);
    }
  }
  return {
    totalCandidatePairs: relations.length,
    scoredPairs: buckets.high + buckets.medium + buckets.low,
    unscoredPairs: buckets.unscored,
    coveredRows: coveredRows.size,
    modelPairs,
    identityPairs,
    manualPairs,
    unknownPairs,
    buckets,
  };
}

const targetLabels: Readonly<Record<string, string>> = {
  party: 'طرف‌حساب',
  uom: 'واحد اندازه‌گیری',
  product: 'کالا',
  warehouse: 'انبار',
  'sales-order': 'سفارش پیش‌نویس',
  'historical-invoice': 'افتتاحیه پیش‌نویس یا مستند فاکتور تسویه‌شده',
  'stock-opening': 'موجودی افتتاحیه پیش‌نویس',
};

function reconciliation(
  rows: readonly ReviewRow[],
  destinations: readonly ImportDestination[],
  hasResult: boolean,
) {
  const sourceIds = new Set(rows.map((row) => row.rowId));
  const matches = destinations.filter((item) => sourceIds.has(item.sourceRowId));
  const mappedIds = new Set(matches.map((item) => item.sourceRowId));
  // Many source lines may refer to one target. Count entities separately from source coverage.
  const uniqueTargets = (items: readonly ImportDestination[]) =>
    new Set(items.map((item) => `${item.kind}:${item.entityId}`)).size;
  const masters = matches.filter((item) =>
    ['party', 'uom', 'product', 'warehouse'].includes(item.kind),
  );
  return {
    available: hasResult,
    sourceRows: rows.length,
    mappedSourceRows: mappedIds.size,
    unmappedSourceRows: hasResult ? rows.filter((row) => !mappedIds.has(row.rowId)).length : null,
    targetMappings: matches.length,
    outcome: {
      mastersCreated: uniqueTargets(masters.filter((item) => item.status === 'created')),
      mastersExisting: uniqueTargets(masters.filter((item) => item.status === 'existing')),
      draftOrders: uniqueTargets(
        matches.filter((item) => item.kind === 'sales-order' && item.status === 'draft'),
      ),
      draftOpeningRows: new Set(
        matches
          .filter((item) => item.kind === 'opening' && item.status === 'draft')
          .map((item) => item.sourceRowId),
      ).size,
      retainedSourceRows: new Set(
        matches
          .filter((item) => item.kind === 'source-evidence' && item.status === 'retained')
          .map((item) => item.sourceRowId),
      ).size,
    },
  };
}

/** Counts evidence from the current revision only; validation never implies JEV confidence. */
export function buildImportReview(workspace: ReviewWorkspace, packs: readonly GuidedImportPack[]) {
  const rows = workspace.rows;
  const rowIds = new Set(rows.map((row) => row.rowId));
  const relations = workspace.proposals.filter(
    (relation) => rowIds.has(relation.leftRowId) && rowIds.has(relation.rightRowId),
  );
  const destinations = workspace.result?.destinations ?? [];
  const hasResult = Array.isArray(workspace.result?.destinations);
  const packIds = new Set([
    ...(workspace.files?.map((file) => file.packId) ?? []),
    ...rows.map((row) => row.packId),
  ]);
  const sections = [...packIds].map((packId) => {
    const pack = packs.find((item) => item.id === packId);
    const sectionRows = rows.filter((row) => row.packId === packId);
    const sectionIds = new Set(sectionRows.map((row) => row.rowId));
    const sectionRelations = relations.filter(
      (relation) => sectionIds.has(relation.leftRowId) || sectionIds.has(relation.rightRowId),
    );
    const sheetIds = new Set([
      ...(pack?.sheets.map((sheet) => sheet.id) ?? []),
      ...sectionRows.map((row) => row.sheetId),
    ]);
    const sheets = [...sheetIds].map((sheetId) => {
      const sheet = pack?.sheets.find((item) => item.id === sheetId);
      const sheetRows = sectionRows.filter((row) => row.sheetId === sheetId);
      const validRows = sheetRows.filter((row) => row.errors.length === 0).length;
      return {
        sheetId,
        label: sheet?.name ?? sheetId,
        targetLabel: targetLabels[sheet?.entityClass ?? ''] ?? 'نیازمند بررسی مقصد',
        totalRows: sheetRows.length,
        validRows,
        errorRows: sheetRows.length - validRows,
      };
    });
    const validRows = sectionRows.filter((row) => row.errors.length === 0).length;
    const reconciled = reconciliation(sectionRows, destinations, hasResult);
    return {
      packId,
      label: pack?.label ?? packId,
      fileName: workspace.files?.find((file) => file.packId === packId)?.fileName ?? null,
      sheets,
      targetLabels: [
        ...new Set(sheets.filter((sheet) => sheet.totalRows > 0).map((sheet) => sheet.targetLabel)),
      ],
      sourceRows: sectionRows.length,
      validRows,
      errorRows: sectionRows.length - validRows,
      pendingRelations: sectionRelations.filter((relation) => relation.decision === 'pending')
        .length,
      conflicts: sectionRelations.filter(
        (relation) => relation.strongConflict && relation.decision !== 'separate',
      ).length,
      model: modelCoverage(sectionRelations, sectionIds),
      reconciliation: reconciled,
      outcome: reconciled.outcome,
    };
  });
  const validRows = rows.filter((row) => row.errors.length === 0).length;
  const reconciled = reconciliation(rows, destinations, hasResult);
  return {
    totalRows: rows.length,
    validRows,
    errorRows: rows.length - validRows,
    pendingRelations: relations.filter((relation) => relation.decision === 'pending').length,
    conflicts: relations.filter(
      (relation) => relation.strongConflict && relation.decision !== 'separate',
    ).length,
    reviewedRelations: relations.filter((relation) => relation.decision !== 'pending').length,
    linkedRelations: relations.filter((relation) => relation.decision === 'link').length,
    separateRelations: relations.filter((relation) => relation.decision === 'separate').length,
    referenceLinks:
      workspace.referenceLinks?.filter(
        (link) => rowIds.has(link.fromRowId) && rowIds.has(link.toRowId),
      ).length ?? 0,
    model: modelCoverage(relations),
    sections,
    reconciliation: reconciled,
    outcome: reconciled.outcome,
  };
}
